import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, ShieldCheck, ShieldAlert, ShieldX, AlertCircle } from 'lucide-react';
import { certificateCheckApi } from '../services/api';
import AfricaNendaLogo from '../components/AfricaNendaLogo';

/**
 * The page a printed certificate's QR code opens: /verify/<reportId>?h=<hash fragment>.
 *
 * Public — whoever holds the paper is not signed in. The engine answers only to the
 * report id together with the hash fragment printed in the code, and repeats only
 * what the certificate itself states.
 */
const OUTCOMES = {
  VALID: {
    Icon: ShieldCheck, tone: 'text-green-700 bg-green-50 ring-green-200',
    title: 'Authentic certificate',
    text: 'AfricaNenda issued this certificate and it is valid. The details below are those on record; they should match the printed certificate.',
  },
  EXPIRED: {
    Icon: ShieldAlert, tone: 'text-amber-700 bg-amber-50 ring-amber-200',
    title: 'Authentic, but expired',
    text: 'AfricaNenda issued this certificate, but it is past its expiry date. The participant needs to certify again.',
  },
  REVOKED: {
    Icon: ShieldX, tone: 'text-red-700 bg-red-50 ring-red-200',
    title: 'Revoked',
    text: 'AfricaNenda issued this certificate and has since revoked it. It no longer stands. The participant needs to certify again.',
  },
  NOT_CERTIFIED: {
    Icon: ShieldX, tone: 'text-red-700 bg-red-50 ring-red-200',
    title: 'Not a valid certificate',
    text: 'The certification run this code refers to exists, but no approval stands behind it.',
  },
  NOT_FOUND: {
    Icon: ShieldX, tone: 'text-red-700 bg-red-50 ring-red-200',
    title: 'Not recognised',
    text: 'No AfricaNenda IIPS Sandbox certificate matches this code. Scan the code on the original certificate: a copied or altered certificate will not match.',
  },
};

const day = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const moment = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : iso;
};

const DECISIONS = { APPROVED: 'Certified', APPROVED_WITH_CONDITIONS: 'Certified with conditions' };

function Row({ label, children }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[8.5rem_1fr] gap-0.5 sm:gap-3 py-2 border-b border-gray-100 last:border-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-primary-700 pt-0.5">{label}</dt>
      <dd className="text-sm text-gray-900">{children}</dd>
    </div>
  );
}

export default function VerifyCertificate() {
  const { reportId } = useParams();
  const [search] = useSearchParams();
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    certificateCheckApi.verify(reportId, search.get('h'))
      .then(r => setResult(r || { status: 'NOT_FOUND', reportId }))
      .catch(() => setError('The check could not be completed just now. Please try again in a moment.'));
  }, [reportId, search]);

  const outcome = result ? (OUTCOMES[result.status] || OUTCOMES.NOT_FOUND) : null;
  const detailed = result && (result.status === 'VALID' || result.status === 'EXPIRED');
  // A revoked certificate names its holder and when it was revoked, so whoever
  // holds the paper can tell which one it was; who revoked it and why stay private.
  const revoked = result?.status === 'REVOKED';

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-100 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-xl p-8">
        <div className="flex flex-col items-center mb-6">
          <AfricaNendaLogo className="h-9" />
          <p className="mt-3 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700">
            AfricaNenda IIPS Sandbox · Certificate check
          </p>
        </div>

        {!result && !error && (
          <div className="py-8 text-gray-500 flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Checking the certificate…
          </div>
        )}

        {error && (
          <div className="text-center py-4">
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <p className="text-sm text-gray-600">{error}</p>
          </div>
        )}

        {outcome && (
          <>
            <div className="text-center">
              <div className={`inline-flex w-16 h-16 rounded-full items-center justify-center ring-4 ${outcome.tone}`}>
                <outcome.Icon className="w-8 h-8" />
              </div>
              <h1 className="mt-4 text-xl font-bold text-primary-900">{outcome.title}</h1>
              <p className="mt-2 text-sm text-gray-600">{outcome.text}</p>
              {result.participantActive === false && (
                <p className="mt-3 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                  This participant is no longer active in the AfricaNenda IIPS Sandbox. The certificate is shown as it was
                  issued.
                </p>
              )}
            </div>

            {detailed && (
              <dl className="mt-6 rounded-lg border border-gray-200 px-4 py-1">
                <Row label="Participant">{result.participant}</Row>
                <Row label="FSP id"><span className="font-mono">{result.fspId}</span></Row>
                <Row label="Corridor">{result.corridor}</Row>
                <Row label="Verdict">
                  {result.verdict}
                  {result.mandatoryTotal != null && ` · ${result.mandatoryPassed} / ${result.mandatoryTotal} mandatory exchanges passed`}
                </Row>
                <Row label={DECISIONS[result.decision] ? `${DECISIONS[result.decision]} by` : 'Decision'}>
                  {result.authorisingOfficer}
                  {result.officerTitle && <span className="block text-xs text-gray-500">{result.officerTitle}</span>}
                  {result.decidedAt && <span className="block text-xs text-gray-500">on {day(result.decidedAt)}</span>}
                </Row>
                <Row label="Issued by">{result.issuedBy}</Row>
                <Row label="Issued">{day(result.issuedAt)}</Row>
                <Row label={result.status === 'EXPIRED' ? 'Expired' : 'Valid until'}>{day(result.expiresAt)}</Row>
              </dl>
            )}

            {revoked && (
              <dl className="mt-6 rounded-lg border border-gray-200 px-4 py-1">
                <Row label="Participant">{result.participant}</Row>
                <Row label="FSP id">{result.fspId && <span className="font-mono">{result.fspId}</span>}</Row>
                <Row label="Corridor">{result.corridor}</Row>
                <Row label="Issued by">{result.issuedBy}</Row>
                <Row label="Issued">{day(result.issuedAt)}</Row>
                <Row label="Revoked">{day(result.decidedAt)}</Row>
              </dl>
            )}

            <div className="mt-6 text-center text-xs text-gray-400 space-y-1">
              <p>Report <span className="font-mono text-gray-500 break-all">{result.reportId || reportId}</span></p>
              {result.checkedAt && <p>Checked {moment(result.checkedAt)}</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
